import { create } from 'zustand';
import { sharedService, type ReligionWithMeta, type SectWithMeta } from '../services/sharedService';
import type { SelectGroup, SelectOption } from '../components';
import type { PlanCurrencyCode } from '../types/api';
import {
  DEFAULT_PRICING_CURRENCY,
  mapUserLocationToCurrency,
} from '../utils/pricingCurrency';

interface SharedState {
  // Loading states
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // ============ Static Lookups (loaded at app startup) ============
  
  // Basic
  heights: SelectOption[];
  motherTongues: SelectGroup[];
  religions: SelectOption[];
  religionsWithMeta: ReligionWithMeta[]; // For dynamic form logic
  
  // Location
  countries: SelectOption[];
  userLocation: string | null;
  pricingCurrency: PlanCurrencyCode;
  
  // Personal Details
  maritalStatuses: SelectOption[];
  complexions: SelectOption[];
  bodyTypes: SelectOption[];
  disabilities: SelectOption[];
  bloodGroups: SelectOption[];
  
  // Income & Education
  // annualIncomes: SelectOption[];
  degrees: SelectGroup[];
  employedIn: SelectOption[];
  
  // Family
  familyTypes: SelectOption[];
  familyValues: SelectOption[];
  familyStatuses: SelectOption[];
  fatherOccupations: SelectOption[];
  motherOccupations: SelectOption[];
  
  // Lifestyle
  diets: SelectOption[];
  drinkingHabits: SelectOption[];
  smokingHabits: SelectOption[];
  
  // Horoscope
  stars: SelectOption[];
  raasis: SelectOption[];
  doshas: SelectOption[];
  manglikStatuses: SelectOption[];
  
  // Profile
  profileCreatedFor: SelectOption[];
  hobbies: SelectOption[];
  interests: SelectOption[];

  // ============ Dependent Lookups (fetched on demand) ============
  castes: SelectOption[];
  subCastes: SelectOption[];
  sects: SelectOption[];
  sectsWithMeta: SectWithMeta[]; // For jamat logic
  jamats: SelectOption[];
  gotras: SelectOption[];
  states: SelectOption[];
  cities: SelectOption[];
  occupations: SelectGroup[];

  // ============ Actions ============
  initialize: () => Promise<void>;
  
  // Dependent lookup fetchers
  fetchCastes: (religionId: string, sectId?: string) => Promise<void>;
  fetchSubCastes: (casteId: string) => Promise<void>;
  fetchSects: (religionId: string) => Promise<void>;
  fetchSectsWithMeta: (religionId: string) => Promise<SectWithMeta[]>;
  fetchJamats: (sectId: string) => Promise<void>;
  fetchGotras: (religionId: string) => Promise<void>;
  fetchStates: (countryId: string) => Promise<void>;
  fetchCities: (stateId: string) => Promise<void>;
  fetchOccupations: (employedInId: string) => Promise<void>;
  
  // Helper to get religion metadata
  getReligionMeta: (religionId: string) => ReligionWithMeta | undefined;
  getSectMeta: (sectId: string) => SectWithMeta | undefined;
  
  clearDependentLookups: (type: 'castes' | 'subCastes' | 'sects' | 'sectsWithMeta' | 'jamats' | 'gotras' | 'states' | 'cities' | 'occupations') => void;
}

export const useSharedStore = create<SharedState>()((set, get) => ({
  // Initial states
  isLoading: false,
  isInitialized: false,
  error: null,

  // ============ Empty Initial Data ============
  
  // Basic
  heights: [],
  motherTongues: [],
  religions: [],
  religionsWithMeta: [],
  
  // Location
  countries: [],
  userLocation: null,
  pricingCurrency: DEFAULT_PRICING_CURRENCY,
  
  // Personal Details
  maritalStatuses: [],
  complexions: [],
  bodyTypes: [],
  disabilities: [],
  bloodGroups: [],
  
  // Income & Education
  // annualIncomes: [],
  degrees: [],
  employedIn: [],
  
  // Family
  familyTypes: [],
  familyValues: [],
  familyStatuses: [],
  fatherOccupations: [],
  motherOccupations: [],
  
  // Lifestyle
  diets: [],
  drinkingHabits: [],
  smokingHabits: [],
  
  // Horoscope
  stars: [],
  raasis: [],
  doshas: [],
  manglikStatuses: [],
  
  // Profile
  profileCreatedFor: [],
  hobbies: [],
  interests: [],
  
  // Dependent lookups
  castes: [],
  subCastes: [],
  sects: [],
  sectsWithMeta: [],
  jamats: [],
  gotras: [],
  states: [],
  cities: [],
  occupations: [],

  // ============ Initialize all static data at app startup ============
  initialize: async () => {
    // Skip if already initialized or loading
    if (get().isInitialized || get().isLoading) {
      console.log('[SharedStore] Skipping init - already initialized:', get().isInitialized, 'loading:', get().isLoading);
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const [
        // Basic
        heights,
        motherTongues,
        religions,
        religionsWithMeta,
        // Location
        countries,
        locationResponse,
        // Personal Details
        maritalStatuses,
        complexions,
        bodyTypes,
        disabilities,
        bloodGroups,
        // Income & Education
        // annualIncomes,
        degrees,
        employedInData,
        // Family
        familyTypes,
        fatherOccupations,
        motherOccupations,
        // Lifestyle
        diets,
        drinkingHabits,
        smokingHabits,
        // Profile
        hobbies,
      ] = await Promise.all([
        // Basic
        sharedService.getHeights(),
        sharedService.getMotherTongues(),
        sharedService.getReligions(),
        sharedService.getReligionsWithMeta(),
        // Location
        sharedService.getCountries(),
        sharedService.getUserLocation().catch(() => ({ userLocation: '' })),
        // Personal Details
        sharedService.getMaritalStatuses(),
        sharedService.getComplexions(),
        sharedService.getBodyTypes(),
        sharedService.getDisabilities(),
        sharedService.getBloodGroups(),
        // Income & Education
        // sharedService.getAnnualIncomes(),
        sharedService.getDegrees(),
        sharedService.getEmployedIn(),
        // Family
        sharedService.getFamilyTypes(),
        sharedService.getFatherOccupations(),
        sharedService.getMotherOccupations(),
        // Lifestyle
        sharedService.getDiets(),
        sharedService.getDrinkingHabits(),
        sharedService.getSmokingHabits(),
        // Profile
        sharedService.getHobbies(),
      ]);

      const userLocation = locationResponse.userLocation || null;
      const pricingCurrency = userLocation
        ? mapUserLocationToCurrency(userLocation)
        : DEFAULT_PRICING_CURRENCY;

      console.log('[SharedStore] Data loaded:', {
        heights: heights.length,
        motherTongues: motherTongues.length,
        religions: religions.length,
        religionsWithMeta: religionsWithMeta.length,
        countries: countries.length,
        maritalStatuses: maritalStatuses.length,
      });

      set({
        // Basic
        heights,
        motherTongues,
        religions,
        religionsWithMeta,
        // Location
        countries,
        userLocation,
        pricingCurrency,
        // Personal Details
        maritalStatuses,
        complexions,
        bodyTypes,
        disabilities,
        bloodGroups,
        // Income & Education
        // annualIncomes,
        degrees,
        employedIn: employedInData,
        // Family
        familyTypes,
        fatherOccupations,
        motherOccupations,
        // Lifestyle
        diets,
        drinkingHabits,
        smokingHabits,
        // Profile
        hobbies,
        // State
        isLoading: false,
        isInitialized: true,
        error: null,
      });
    } catch (error) {
      console.error('Failed to initialize shared data:', error);
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load shared data',
      });
    }
  },

  // ============ Dependent Lookup Fetchers ============

  fetchCastes: async (religionId: string, sectId?: string) => {
    try {
      const castes = await sharedService.getCastes(religionId, sectId);
      set({ castes });
    } catch (error) {
      console.error('Failed to fetch castes:', error);
      set({ castes: [] });
    }
  },

  fetchSubCastes: async (casteId: string) => {
    try {
      const subCastes = await sharedService.getSubCastes(casteId);
      set({ subCastes });
    } catch (error) {
      console.error('Failed to fetch sub-castes:', error);
      set({ subCastes: [] });
    }
  },

  fetchSects: async (religionId: string) => {
    try {
      const sects = await sharedService.getSects(religionId);
      set({ sects });
    } catch (error) {
      console.error('Failed to fetch sects:', error);
      set({ sects: [] });
    }
  },

  fetchSectsWithMeta: async (religionId: string) => {
    try {
      const sectsWithMeta = await sharedService.getSectsWithMeta(religionId);
      // Also set the simple sects for dropdown
      const sects = sectsWithMeta.map(s => ({ value: s.sect_id, label: s.name }));
      set({ sectsWithMeta, sects });
      return sectsWithMeta;
    } catch (error) {
      console.error('Failed to fetch sects with meta:', error);
      set({ sectsWithMeta: [], sects: [] });
      return [];
    }
  },

  fetchJamats: async (sectId: string) => {
    try {
      const jamats = await sharedService.getJamats(sectId);
      set({ jamats });
    } catch (error) {
      console.error('Failed to fetch jamats:', error);
      set({ jamats: [] });
    }
  },

  fetchGotras: async (religionId: string) => {
    try {
      const gotras = await sharedService.getGotras(religionId);
      set({ gotras });
    } catch (error) {
      console.error('Failed to fetch gotras:', error);
      set({ gotras: [] });
    }
  },

  fetchStates: async (countryId: string) => {
    try {
      const states = await sharedService.getStates(countryId);
      set({ states });
    } catch (error) {
      console.error('Failed to fetch states:', error);
      set({ states: [] });
    }
  },

  fetchCities: async (stateId: string) => {
    try {
      const cities = await sharedService.getCities(stateId);
      set({ cities });
    } catch (error) {
      console.error('Failed to fetch cities:', error);
      set({ cities: [] });
    }
  },

  fetchOccupations: async (employedInId: string) => {
    try {
      const occupations = await sharedService.getOccupations(employedInId);
      set({ occupations });
    } catch (error) {
      console.error('Failed to fetch occupations:', error);
      set({ occupations: [] });
    }
  },

  // Helper to get religion metadata by ID
  getReligionMeta: (religionId: string) => {
    return get().religionsWithMeta.find(r => r.religion_id === religionId);
  },

  // Helper to get sect metadata by ID
  getSectMeta: (sectId: string) => {
    return get().sectsWithMeta.find(s => s.sect_id === sectId);
  },

  // Clear dependent lookups
  clearDependentLookups: (type) => {
    set({ [type]: [] });
  },
}));
